import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, firestore } from '../../firebase';
import { createRealtimeCall } from '../../utils/realtimeApi';

const DEFAULT_REALTIME_VOICE = 'marin';
const DEFAULT_REALTIME_MODEL = 'gpt-realtime';
const WEEKLY_VOICE_LIMIT_SECONDS = 5 * 60;
const VOICE_USAGE_COLLECTION = 'model_usage_by_user';
const VOICE_USAGE_FIELD = 'realtimeVoiceUsage';
const USAGE_TICK_INTERVAL_MS = 250;
const MIN_USAGE_DELTA_SECONDS = 0.25;
const WEEK_CHECK_INTERVAL_MS = 1000;

const getEmptyTranscriptState = () => ({
  user: '',
  assistant: '',
});

const buildRealtimeSessionConfig = ({ model, voice, instructions } = {}) => ({
  type: 'realtime',
  model: model || DEFAULT_REALTIME_MODEL,
  ...(instructions ? { instructions } : {}),
  audio: {
    input: {
      turn_detection: {
        type: 'server_vad',
      },
      transcription: {
        model: 'gpt-4o-mini-transcribe',
      },
    },
    output: {
      voice: voice || DEFAULT_REALTIME_VOICE,
    },
  },
});

const normalizeEventType = (eventData) =>
  typeof eventData?.type === 'string' ? eventData.type.toLowerCase() : '';

const extractTranscriptText = (eventData, fallbackField = 'transcript') => {
  const fromField = eventData?.[fallbackField];
  if (typeof fromField === 'string' && fromField.trim()) {
    return fromField.trim();
  }

  const content = eventData?.item?.content;
  if (Array.isArray(content)) {
    for (const item of content) {
      if (typeof item?.transcript === 'string' && item.transcript.trim()) {
        return item.transcript.trim();
      }
      if (typeof item?.text === 'string' && item.text.trim()) {
        return item.text.trim();
      }
    }
  }

  return '';
};

const buildTranscriptEventId = ({ eventData, transcript, role, eventType }) => {
  if (typeof eventData?.event_id === 'string' && eventData.event_id) {
    return eventData.event_id;
  }
  const itemId = eventData?.item_id || eventData?.item?.id || '';
  const responseId = eventData?.response_id || '';
  const outputIndex =
    typeof eventData?.output_index === 'number' ? String(eventData.output_index) : '';
  return `${role}:${eventType}:${itemId}:${responseId}:${outputIndex}:${transcript}`;
};

const clampUsageSeconds = (value) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(WEEKLY_VOICE_LIMIT_SECONDS, value));
};

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentWeekStartKey = (date = new Date()) => {
  const weekStart = new Date(date);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  return toLocalDateKey(weekStart);
};

export const useRealtimeVoice = ({
  onUserTranscriptFinal,
  onAssistantTranscriptFinal,
} = {}) => {
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const processedTranscriptEventIdsRef = useRef(new Set());
  const userTranscriptCallbackRef = useRef(onUserTranscriptFinal);
  const assistantTranscriptCallbackRef = useRef(onAssistantTranscriptFinal);
  const usageSnapshotRef = useRef({
    weekStart: getCurrentWeekStartKey(),
    usedSeconds: 0,
  });
  const usagePersistChainRef = useRef(Promise.resolve());
  const usageTickIntervalRef = useRef(null);
  const activeUsageStartedAtRef = useRef(null);
  const usageLoadRequestIdRef = useRef(0);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [status, setStatus] = useState('Idle');
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState(getEmptyTranscriptState());
  const [weeklyUsedSeconds, setWeeklyUsedSeconds] = useState(0);
  const [weeklyUsageLoaded, setWeeklyUsageLoaded] = useState(false);
  const [isUsageTrackingActive, setIsUsageTrackingActive] = useState(false);
  const [activeUsageElapsedMs, setActiveUsageElapsedMs] = useState(0);
  const [currentWeekStartKey, setCurrentWeekStartKey] = useState(() =>
    getCurrentWeekStartKey()
  );

  useEffect(() => {
    userTranscriptCallbackRef.current = onUserTranscriptFinal;
  }, [onUserTranscriptFinal]);

  useEffect(() => {
    assistantTranscriptCallbackRef.current = onAssistantTranscriptFinal;
  }, [onAssistantTranscriptFinal]);

  const hasProcessedTranscriptEvent = useCallback((eventId) => {
    if (!eventId) return false;
    const processedIds = processedTranscriptEventIdsRef.current;
    if (processedIds.has(eventId)) {
      return true;
    }
    processedIds.add(eventId);
    if (processedIds.size > 500) {
      const recentIds = Array.from(processedIds).slice(-250);
      processedTranscriptEventIdsRef.current = new Set(recentIds);
    }
    return false;
  }, []);

  const isSupported = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return Boolean(
      window.RTCPeerConnection &&
        window.MediaStream &&
        navigator?.mediaDevices?.getUserMedia
    );
  }, []);

  const loadWeeklyUsage = useCallback(
    async (userId) => {
      const requestId = usageLoadRequestIdRef.current + 1;
      usageLoadRequestIdRef.current = requestId;

      const currentWeekStart = getCurrentWeekStartKey();
      const initialSnapshot = {
        weekStart: currentWeekStart,
        usedSeconds: 0,
      };

      if (usageLoadRequestIdRef.current !== requestId) {
        return;
      }

      usageSnapshotRef.current = initialSnapshot;
      setWeeklyUsedSeconds(0);
      setWeeklyUsageLoaded(false);

      if (!userId) {
        setWeeklyUsageLoaded(true);
        return;
      }

      try {
        const usageRef = doc(firestore, VOICE_USAGE_COLLECTION, userId);
        const usageDoc = await getDoc(usageRef);

        let remoteSnapshot = {
          weekStart: currentWeekStart,
          usedSeconds: 0,
        };

        if (usageDoc.exists()) {
          const usageData = usageDoc.data() || {};
          const remoteVoiceUsage = usageData[VOICE_USAGE_FIELD] || {};
          const storedWeekStart =
            typeof remoteVoiceUsage.weekStart === 'string'
              ? remoteVoiceUsage.weekStart
              : '';
          const storedUsedSeconds = clampUsageSeconds(
            Number(remoteVoiceUsage.usedSeconds)
          );

          if (storedWeekStart === currentWeekStart) {
            remoteSnapshot = {
              weekStart: currentWeekStart,
              usedSeconds: storedUsedSeconds,
            };
          } else {
            await setDoc(
              usageRef,
              {
                [VOICE_USAGE_FIELD]: {
                  weekStart: currentWeekStart,
                  usedSeconds: 0,
                  updatedAt: serverTimestamp(),
                },
              },
              { merge: true }
            );
          }
        } else {
          await setDoc(
            usageRef,
            {
              [VOICE_USAGE_FIELD]: {
                weekStart: currentWeekStart,
                usedSeconds: 0,
                updatedAt: serverTimestamp(),
              },
            },
            { merge: true }
          );
        }

        if (usageLoadRequestIdRef.current !== requestId) {
          return;
        }

        usageSnapshotRef.current = remoteSnapshot;
        setWeeklyUsedSeconds(remoteSnapshot.usedSeconds);
      } catch (usageLoadError) {
        console.error('Failed to load realtime voice usage:', usageLoadError);
      } finally {
        if (usageLoadRequestIdRef.current === requestId) {
          setWeeklyUsageLoaded(true);
        }
      }
    },
    []
  );

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      void loadWeeklyUsage(user?.uid || null);
    });
    return () => unsubscribe();
  }, [loadWeeklyUsage]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const nextWeekStartKey = getCurrentWeekStartKey();
      setCurrentWeekStartKey((previousWeekStartKey) =>
        previousWeekStartKey === nextWeekStartKey
          ? previousWeekStartKey
          : nextWeekStartKey
      );
    }, WEEK_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (usageSnapshotRef.current.weekStart === currentWeekStartKey) {
      return;
    }

    void loadWeeklyUsage(auth.currentUser?.uid || null);
  }, [currentWeekStartKey, loadWeeklyUsage]);

  const stopUsageTrackingTimer = useCallback(() => {
    if (usageTickIntervalRef.current) {
      clearInterval(usageTickIntervalRef.current);
      usageTickIntervalRef.current = null;
    }
    activeUsageStartedAtRef.current = null;
    setIsUsageTrackingActive(false);
    setActiveUsageElapsedMs(0);
  }, []);

  const startUsageTrackingTimer = useCallback(() => {
    if (activeUsageStartedAtRef.current) {
      return;
    }

    activeUsageStartedAtRef.current = Date.now();
    setIsUsageTrackingActive(true);
    setActiveUsageElapsedMs(0);

    if (usageTickIntervalRef.current) {
      clearInterval(usageTickIntervalRef.current);
    }

    usageTickIntervalRef.current = setInterval(() => {
      const startedAt = activeUsageStartedAtRef.current;
      if (!startedAt) {
        return;
      }
      setActiveUsageElapsedMs(Date.now() - startedAt);
    }, USAGE_TICK_INTERVAL_MS);
  }, []);

  const persistUsageIncrement = useCallback(
    async (elapsedSeconds) => {
      const deltaSeconds = Math.max(0, elapsedSeconds);
      if (deltaSeconds < MIN_USAGE_DELTA_SECONDS) {
        return;
      }

      const userId = auth.currentUser?.uid || null;
      const currentWeekStart = getCurrentWeekStartKey();
      const currentSnapshot = usageSnapshotRef.current;
      const previousUsedSeconds =
        currentSnapshot.weekStart === currentWeekStart
          ? currentSnapshot.usedSeconds
          : 0;
      const localNextUsedSeconds = clampUsageSeconds(previousUsedSeconds + deltaSeconds);
      const localSnapshot = {
        weekStart: currentWeekStart,
        usedSeconds: localNextUsedSeconds,
      };

      usageSnapshotRef.current = localSnapshot;
      setWeeklyUsedSeconds(localNextUsedSeconds);

      if (!userId) {
        return;
      }

      usagePersistChainRef.current = usagePersistChainRef.current
        .then(async () => {
          const usageRef = doc(firestore, VOICE_USAGE_COLLECTION, userId);

          const syncedUsedSeconds = await runTransaction(firestore, async (transaction) => {
            const usageDoc = await transaction.get(usageRef);

            let remoteWeekStart = currentWeekStart;
            let remoteUsedSeconds = 0;

            if (usageDoc.exists()) {
              const usageData = usageDoc.data() || {};
              const remoteVoiceUsage = usageData[VOICE_USAGE_FIELD] || {};
              remoteWeekStart =
                typeof remoteVoiceUsage.weekStart === 'string'
                  ? remoteVoiceUsage.weekStart
                  : currentWeekStart;
              remoteUsedSeconds = clampUsageSeconds(
                Number(remoteVoiceUsage.usedSeconds)
              );
            }

            const normalizedRemoteUsedSeconds =
              remoteWeekStart === currentWeekStart ? remoteUsedSeconds : 0;
            const baseUsedSeconds = Math.max(normalizedRemoteUsedSeconds, previousUsedSeconds);
            const nextUsedSeconds = clampUsageSeconds(baseUsedSeconds + deltaSeconds);

            transaction.set(
              usageRef,
              {
                [VOICE_USAGE_FIELD]: {
                  weekStart: currentWeekStart,
                  usedSeconds: nextUsedSeconds,
                  updatedAt: serverTimestamp(),
                },
              },
              { merge: true }
            );

            return nextUsedSeconds;
          });

          const syncedSnapshot = {
            weekStart: currentWeekStart,
            usedSeconds: syncedUsedSeconds,
          };
          usageSnapshotRef.current = syncedSnapshot;
          setWeeklyUsedSeconds(syncedUsedSeconds);
        })
        .catch((persistError) => {
          console.error('Failed to persist realtime voice usage:', persistError);
        });

      return usagePersistChainRef.current;
    },
    []
  );

  const flushActiveSessionUsage = useCallback(async () => {
    const startedAt = activeUsageStartedAtRef.current;
    if (!startedAt) {
      stopUsageTrackingTimer();
      return;
    }

    const elapsedSeconds = (Date.now() - startedAt) / 1000;
    stopUsageTrackingTimer();
    await persistUsageIncrement(elapsedSeconds);
  }, [persistUsageIncrement, stopUsageTrackingTimer]);

  const {
    currentWeekUsedSeconds,
    weeklyRemainingSeconds,
    hasReachedWeeklyLimit,
  } = useMemo(() => {
    const normalizedUsedSeconds =
      usageSnapshotRef.current.weekStart === currentWeekStartKey
        ? clampUsageSeconds(weeklyUsedSeconds)
        : 0;
    const activeSessionElapsedSeconds = isUsageTrackingActive
      ? activeUsageElapsedMs / 1000
      : 0;
    const remainingSeconds = Math.max(
      0,
      WEEKLY_VOICE_LIMIT_SECONDS - normalizedUsedSeconds - activeSessionElapsedSeconds
    );

    return {
      currentWeekUsedSeconds: normalizedUsedSeconds,
      weeklyRemainingSeconds: remainingSeconds,
      hasReachedWeeklyLimit: remainingSeconds <= 0,
    };
  }, [
    activeUsageElapsedMs,
    currentWeekStartKey,
    isUsageTrackingActive,
    weeklyUsedSeconds,
  ]);

  const cleanupConnection = useCallback(() => {
    void flushActiveSessionUsage();

    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (error) {
        // Ignore channel close errors during teardown.
      }
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (error) {
        // Ignore peer connection close errors during teardown.
      }
      peerConnectionRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (remoteAudioRef.current) {
      try {
        remoteAudioRef.current.pause();
      } catch (error) {
        // Ignore audio pause errors during teardown.
      }
      remoteAudioRef.current.srcObject = null;
    }

    setIsConnecting(false);
    setIsConnected(false);
    setIsMuted(false);
  }, [flushActiveSessionUsage]);

  const handleRealtimeEvent = useCallback((eventData) => {
    const eventType = normalizeEventType(eventData);
    if (!eventType) return;

    if (eventType.includes('error')) {
      const message =
        eventData?.error?.message ||
        eventData?.message ||
        'Realtime voice returned an error';
      setError(message);
      return;
    }

    if (
      eventType === 'response.audio_transcript.delta' ||
      eventType === 'response.output_audio_transcript.delta'
    ) {
      if (typeof eventData?.delta === 'string') {
        setTranscript((prev) => ({
          ...prev,
          assistant: `${prev.assistant}${eventData.delta}`,
        }));
      }
      return;
    }

    if (
      eventType === 'response.audio_transcript.done' ||
      eventType === 'response.output_audio_transcript.done'
    ) {
      const assistantTranscript = extractTranscriptText(eventData, 'transcript');
      if (assistantTranscript) {
        setTranscript((prev) => ({
          ...prev,
          assistant: assistantTranscript,
        }));
        const eventId = buildTranscriptEventId({
          eventData,
          transcript: assistantTranscript,
          role: 'assistant',
          eventType,
        });
        if (!hasProcessedTranscriptEvent(eventId)) {
          assistantTranscriptCallbackRef.current?.({
            eventId,
            transcript: assistantTranscript,
            itemId: eventData?.item_id || eventData?.item?.id || null,
          });
        }
      }
      return;
    }

    const userTranscript = extractTranscriptText(eventData, 'transcript');
    if (
      (eventType === 'conversation.item.input_audio_transcription.completed' ||
        eventType === 'conversation.item.input_audio_transcription.done' ||
        eventType === 'input_audio_buffer.transcription.completed') &&
      userTranscript
    ) {
      setTranscript((prev) => ({
        ...prev,
        user: userTranscript,
      }));
      const eventId = buildTranscriptEventId({
        eventData,
        transcript: userTranscript,
        role: 'user',
        eventType,
      });
      if (!hasProcessedTranscriptEvent(eventId)) {
        userTranscriptCallbackRef.current?.({
          eventId,
          transcript: userTranscript,
          itemId: eventData?.item_id || eventData?.item?.id || null,
        });
      }
    }
  }, [hasProcessedTranscriptEvent]);

  const startSession = useCallback(
    async ({ model, voice, instructions } = {}) => {
      if (!isSupported) {
        throw new Error('Realtime voice is not supported in this browser');
      }

      if (isConnecting || isConnected) {
        return;
      }

      if (!weeklyUsageLoaded) {
        setStatus('Loading weekly voice usage...');
        setError('Voice usage is still loading. Please try again.');
        return;
      }

      if (hasReachedWeeklyLimit) {
        setStatus('Weekly voice limit reached');
        setError('Weekly voice limit reached (5 minutes).');
        return;
      }

      setError(null);
      setTranscript(getEmptyTranscriptState());
      processedTranscriptEventIdsRef.current.clear();
      setStatus('Requesting microphone access...');
      setIsConnecting(true);

      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        localStreamRef.current = localStream;

        const peerConnection = new RTCPeerConnection();
        peerConnectionRef.current = peerConnection;

        if (!remoteAudioRef.current) {
          remoteAudioRef.current = new Audio();
          remoteAudioRef.current.autoplay = true;
          remoteAudioRef.current.playsInline = true;
        }

        peerConnection.ontrack = (event) => {
          const [remoteStream] = event.streams;
          if (!remoteStream || !remoteAudioRef.current) return;
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(() => {
            setStatus('Connected (tap page if audio is blocked)');
          });
        };

        peerConnection.onconnectionstatechange = () => {
          const state = peerConnection.connectionState;
          if (state === 'connected') {
            setStatus('Connected');
            setIsConnected(true);
            setIsConnecting(false);
            startUsageTrackingTimer();
            return;
          }
          if (state === 'connecting') {
            setStatus('Connecting...');
            return;
          }
          if (state === 'failed' || state === 'closed' || state === 'disconnected') {
            setStatus('Disconnected');
            cleanupConnection();
          }
        };

        localStream.getTracks().forEach((track) => {
          peerConnection.addTrack(track, localStream);
        });

        const dataChannel = peerConnection.createDataChannel('oai-events');
        dataChannelRef.current = dataChannel;

        dataChannel.onopen = () => {
          setStatus('Connected');
          setIsConnected(true);
          setIsConnecting(false);
          startUsageTrackingTimer();
        };

        dataChannel.onclose = () => {
          setStatus('Disconnected');
          void flushActiveSessionUsage();
        };

        dataChannel.onerror = () => {
          setError('Realtime data channel error');
        };

        dataChannel.onmessage = (messageEvent) => {
          try {
            const eventData = JSON.parse(messageEvent.data);
            handleRealtimeEvent(eventData);
          } catch (parseError) {
            // Ignore non-JSON payloads from data channel.
          }
        };

        setStatus('Negotiating realtime session...');
        const offer = await peerConnection.createOffer({
          offerToReceiveAudio: true,
        });
        await peerConnection.setLocalDescription(offer);

        const session = buildRealtimeSessionConfig({
          model,
          voice,
          instructions,
        });

        const realtimeCall = await createRealtimeCall({
          sdp: offer.sdp,
          session,
        });

        await peerConnection.setRemoteDescription({
          type: 'answer',
          sdp: realtimeCall.sdp,
        });

        setStatus('Finalizing connection...');
      } catch (sessionError) {
        cleanupConnection();
        setStatus('Idle');
        setError(sessionError?.message || 'Failed to start realtime voice session');
      } finally {
        setIsConnecting(false);
      }
    },
    [
      cleanupConnection,
      flushActiveSessionUsage,
      handleRealtimeEvent,
      hasReachedWeeklyLimit,
      isConnected,
      isConnecting,
      isSupported,
      startUsageTrackingTimer,
      weeklyUsageLoaded,
    ]
  );

  const stopSession = useCallback(() => {
    cleanupConnection();
    processedTranscriptEventIdsRef.current.clear();
    setStatus('Idle');
  }, [cleanupConnection]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;

    const nextMuted = !isMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setIsMuted(nextMuted);
  }, [isMuted]);

  useEffect(() => {
    if (!isConnected || !isUsageTrackingActive) {
      return;
    }

    if (weeklyRemainingSeconds > 0) {
      return;
    }

    setError('Weekly voice limit reached (5 minutes).');
    setStatus('Weekly voice limit reached');
    cleanupConnection();
  }, [cleanupConnection, isConnected, isUsageTrackingActive, weeklyRemainingSeconds]);

  useEffect(() => () => cleanupConnection(), [cleanupConnection]);

  return {
    isSupported,
    isConnecting,
    isConnected,
    isMuted,
    status,
    error,
    transcript,
    startSession,
    stopSession,
    toggleMute,
    weeklyLimitSeconds: WEEKLY_VOICE_LIMIT_SECONDS,
    weeklyUsedSeconds: currentWeekUsedSeconds,
    weeklyRemainingSeconds,
    weeklyUsageLoaded,
    hasReachedWeeklyLimit,
  };
};
